// Stimulus Controller for Bid Form
// Handles bid submission and live updates

import { validateBidAmount, formatBidError } from '../utils/bid_helpers'

export default class BidFormController {
  static values = {
    auctionId: Number,
    minimumBid: Number,
    submitUrl: String
  }

  static targets = ['input', 'submit', 'error', 'success']

  connect() {
    this.listenForBidUpdates()
  }

  listenForBidUpdates() {
    document.addEventListener('auction:bid-update', (event) => {
      // Update minimum bid when a new bid comes in
      this.minimumBidValue = event.detail.auction.minimum_bid
      this.updateInputPlaceholder()
    })

    document.addEventListener('auction:ended', () => {
      this.disableForm()
    })
  }

  updateInputPlaceholder() {
    if (this.hasInputTarget) {
      this.inputTarget.min = this.minimumBidValue
      this.inputTarget.placeholder = `Min: $${this.minimumBidValue.toLocaleString()}`
    }
  }

  async submit(event) {
    event.preventDefault()

    const amount = parseInt(this.inputTarget.value, 10)
    const validation = validateBidAmount(amount, this.minimumBidValue)

    if (!validation.valid) {
      this.showError(validation.errorMessage)
      return
    }

    this.setLoading(true)
    this.clearMessages()

    try {
      const response = await fetch(this.submitUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify({ bid: { amount: amount } })
      })

      const data = await response.json()

      if (response.ok) {
        this.showSuccess('Oferta registrada')
        this.inputTarget.value = ''
        // Update minimum bid for next bid
        if (data.minimum_bid) {
          this.minimumBidValue = data.minimum_bid
          this.updateInputPlaceholder()
        }
      } else {
        this.showError(formatBidError(data.errors))
      }
    } catch (error) {
      this.showError('Error de red. Intenta de nuevo.')
      console.error('Bid submission error:', error)
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(loading) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = loading
      this.submitTarget.textContent = loading ? 'Ofertando...' : 'Ofertar'
    }
    if (this.hasInputTarget) {
      this.inputTarget.disabled = loading
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove('hidden')
    }
  }

  showSuccess(message) {
    if (this.hasSuccessTarget) {
      this.successTarget.textContent = message
      this.successTarget.classList.remove('hidden')
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.successTarget.classList.add('hidden')
      }, 3000)
    }
  }

  clearMessages() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ''
      this.errorTarget.classList.add('hidden')
    }
    if (this.hasSuccessTarget) {
      this.successTarget.classList.add('hidden')
    }
  }

  disableForm() {
    if (this.hasInputTarget) this.inputTarget.disabled = true
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true
      this.submitTarget.textContent = 'Subasta finalizada'
    }
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }
}

// Register with Stimulus if using standard setup
if (typeof window.Stimulus !== 'undefined') {
  window.Stimulus.register('bid-form', BidFormController)
}
